import { useEffect, useState } from "react";
import { ShieldCheck, KeyRound, UserCircle, History, Save, Eye, EyeOff, Check, X, Database, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Account Settings — accessible to Master Admin only.
 * Allows changing the admin's display username and rotating their password.
 * Persists a fresh JWT via AuthContext.setSession() so the session stays valid.
 */
export default function Account() {
  const { user, setSession, refresh } = useAuth();

  const [username, setUsername] = useState(user?.username || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/admin/account/audit-log?limit=10");
      setAuditLog(data);
    } catch (_) {
      // non-fatal
    }
  };

  useEffect(() => {
    setUsername(user?.username || "");
  }, [user?.username]);

  useEffect(() => {
    loadAudit();
  }, []);

  // ---- password complexity hints (mirrors backend _validate_password_complexity) ----
  const pwHasLetter = /[A-Za-z]/.test(newPassword);
  const pwHasDigit = /\d/.test(newPassword);
  const pwHasSymbol = /[^A-Za-z0-9]/.test(newPassword);
  const pwLongEnough = newPassword.length >= 8;
  const pwMatches = newPassword.length > 0 && newPassword === confirmPassword;
  const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;
  const passwordValid = !wantsPasswordChange || (pwLongEnough && pwHasLetter && pwHasDigit && pwHasSymbol && pwMatches);
  const usernameChanged = (username || "").trim() !== (user?.username || "");
  const canSubmit = !saving && (usernameChanged || wantsPasswordChange) && passwordValid;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const payload = {};
    if (usernameChanged) payload.username = username.trim();
    if (wantsPasswordChange) {
      payload.new_password = newPassword;
      payload.confirm_password = confirmPassword;
    }
    try {
      const { data } = await api.put("/admin/account", payload);
      // Refresh session with the newly-issued token + user record.
      setSession(data.access_token, data.user);
      if (usernameChanged) toast.success("Username berhasil diperbarui");
      if (wantsPasswordChange) toast.success("Password berhasil diperbarui");
      setNewPassword("");
      setConfirmPassword("");
      await refresh();
      await loadAudit();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Gagal memperbarui akun";
      toast.error(msg === "Username tidak tersedia" ? "Username tidak tersedia" : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Account Settings</h1>
          <Badge variant="outline" className="border-primary/40 text-primary text-xs">Master Admin</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Kelola username dan password akun Master Admin Anda. Perubahan dicatat dalam audit log.
        </p>
      </div>

      {/* ---- Username & Password Form ---- */}
      <div className="border border-border rounded-lg bg-card p-6 space-y-5" data-testid="account-form">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">
            <UserCircle className="w-3.5 h-3.5" /> Username
          </Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Masukkan username (mis. master.admin)"
            data-testid="account-username-input"
            autoComplete="username"
          />
          <p className="text-xs text-muted-foreground">
            Username yang ditampilkan di dashboard. Email login ({user?.email}) tidak berubah.
          </p>
        </div>

        <div className="border-t border-border pt-5 space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> Password Baru
            </Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 karakter · huruf + angka + simbol"
                data-testid="account-newpw-input"
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="account-toggle-pw"
                aria-label={showPw ? "Sembunyikan password" : "Tampilkan password"}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Konfirmasi Password Baru</Label>
            <Input
              type={showPw ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Ulangi password baru"
              data-testid="account-confirmpw-input"
              autoComplete="new-password"
            />
          </div>

          {/* ---- Complexity checklist ---- */}
          {wantsPasswordChange && (
            <div className="border border-border bg-muted/30 rounded-md p-3 grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs" data-testid="account-pw-rules">
              <Rule ok={pwLongEnough} label="Minimal 8 karakter" />
              <Rule ok={pwHasLetter} label="Mengandung huruf" />
              <Rule ok={pwHasDigit} label="Mengandung angka" />
              <Rule ok={pwHasSymbol} label="Mengandung simbol" />
              <Rule ok={pwMatches} label="Konfirmasi cocok" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-2 border-t border-border">
          <Button onClick={submit} disabled={!canSubmit} data-testid="account-submit">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </div>
      </div>

      {/* ---- Audit Log ---- */}
      <div className="border border-border rounded-lg bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-primary" />
          <h3 className="font-display text-lg font-semibold">Audit Log Akun</h3>
        </div>
        {auditLog.length === 0 ? (
          <div className="text-sm text-muted-foreground">Belum ada perubahan tercatat.</div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="account-audit-log">
            {auditLog.map((row) => (
              <div
                key={row.id}
                className="flex items-start justify-between gap-4 py-2 border-b border-border/60 last:border-0"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {row.action === "password_update" ? "PASSWORD" : "USERNAME"}
                    </Badge>
                    <span className="text-foreground">{row.detail || "—"}</span>
                  </div>
                  {(row.ip || row.user_agent) && (
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      {row.ip && <span>IP {row.ip}</span>}
                      {row.user_agent && <span className="ml-3">{row.user_agent.slice(0, 60)}</span>}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(row.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <LegacyCleanupCard />
    </div>
  );
}

// ---------- Master Admin: Legacy sensor cleanup panel ----------
function LegacyCleanupCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/legacy-sensors/cleanup?dry_run=true");
      setPreview(data);
      if (data.scanned === 0) {
        toast.info("Belum ada sensor di DB");
      } else if (data.deleted === 0 && data.backfilled === 0) {
        toast.success("Semua sensor sudah bersih — tidak perlu cleanup");
      } else {
        toast.success(`Scan selesai: ${data.deleted} akan dihapus · ${data.backfilled} akan di-backfill`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal scan");
    } finally {
      setLoading(false);
    }
  };

  const applyCleanup = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Jalankan cleanup PERMANEN?\n\n` +
      `• ${preview.deleted} dokumen sensor akan DIHAPUS (malformed, tanpa id).\n` +
      `• ${preview.backfilled} dokumen akan diperbarui (backfill field kosong).\n\n` +
      `Aksi ini tidak bisa di-undo. Lanjut?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/legacy-sensors/cleanup");
      toast.success(`Cleanup selesai: ${data.deleted} dihapus · ${data.backfilled} di-backfill`);
      setPreview({ ...data, cleaned: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal apply cleanup");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="border border-border rounded-lg bg-card p-6 mt-6" data-testid="legacy-cleanup-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-md bg-accent/10 text-accent">
          <Database className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold">Cleanup Legacy Sensor Documents</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Scan koleksi <code className="font-mono text-foreground">sensors</code> untuk
            dokumen lama yang shape-nya tidak sesuai model terbaru (tanpa <code>id</code>,
            <code>library_deps=null</code>, dsb). Dry-run dulu — apply hanya jika hasil sesuai.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button
          variant="outline"
          onClick={runScan}
          disabled={loading}
          data-testid="legacy-cleanup-scan-btn"
        >
          <Database className="w-4 h-4 mr-1" /> {loading ? "Memindai…" : "Scan (Dry-run)"}
        </Button>
        {preview && (preview.deleted > 0 || preview.backfilled > 0) && !preview.cleaned && (
          <Button
            variant="default"
            onClick={applyCleanup}
            disabled={applying}
            data-testid="legacy-cleanup-apply-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            {applying ? "Menerapkan…" : `Terapkan cleanup (${preview.deleted + preview.backfilled} dok)`}
          </Button>
        )}
      </div>

      {preview && (
        <div className="border border-border rounded-md p-3 bg-background/40" data-testid="legacy-cleanup-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm">
            <Stat label="Discanned" value={preview.scanned} />
            <Stat label="Perlu dihapus" value={preview.deleted} accent={preview.deleted > 0 ? "warn" : "ok"} />
            <Stat label="Perlu backfill" value={preview.backfilled} accent={preview.backfilled > 0 ? "info" : "ok"} />
            <Stat label="Mode" value={preview.dry_run ? "DRY-RUN" : "APPLIED"} accent={preview.dry_run ? "info" : "ok"} />
          </div>
          {preview.changes.length > 0 && (
            <div className="max-h-56 overflow-y-auto text-xs font-mono space-y-1">
              {preview.changes.map((c, i) => (
                <div key={i} className="flex gap-2 py-0.5">
                  <span className={c.action === "delete" ? "text-destructive" : "text-primary"}>
                    {c.action.toUpperCase()}
                  </span>
                  <span className="text-muted-foreground">{c.type}</span>
                  {c.sensor_id && <span className="text-foreground/70">{c.sensor_id.slice(0, 8)}…</span>}
                  {c.fields && <span className="text-accent">[{c.fields.join(", ")}]</span>}
                  {c.reason && <span className="text-destructive">({c.reason})</span>}
                </div>
              ))}
            </div>
          )}
          {preview.changes.length === 0 && (
            <div className="text-xs text-muted-foreground italic">Tidak ada perubahan yang perlu dilakukan.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "ok" }) {
  const cls = { ok: "text-primary", warn: "text-destructive", info: "text-accent" }[accent];
  return (
    <div>
      <div className="overline text-muted-foreground text-[10px]">{label}</div>
      <div className={`font-display text-2xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-primary" : "text-muted-foreground"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {label}
    </div>
  );
}
